import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, getDocs, query, collection, where } from 'firebase/firestore';
import { firestore } from '../database/firebase';

const UserRoleContext = createContext();

export const useUserRole = () => {
  return useContext(UserRoleContext);
};

export const UserRoleProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!currentUser || !currentUser.email) {
        setUserRole(null);
        setLoading(false);
        return;
      }

      try {
        // Normalize email for consistent matching (trim and lowercase)
        const normalizedEmail = currentUser.email.toLowerCase().trim();

        // Check collections in priority order: alumni > coach > client > admin > moderator > s_admin > developer
        const alumniSnap = await getDocs(
          query(collection(firestore, 'alumni'), where('email', '==', normalizedEmail))
        );
        const coachSnap = await getDocs(
          query(collection(firestore, 'coaches'), where('email', '==', normalizedEmail))
        );
        const clientSnap = await getDocs(
          query(collection(firestore, 'clients'), where('email', '==', normalizedEmail))
        );
        const adminSnap = await getDocs(
          query(collection(firestore, 'admin'), where('email', '==', normalizedEmail))
        );
        const moderatorSnap = await getDocs(
          query(collection(firestore, 'moderator'), where('email', '==', normalizedEmail))
        );
        const superAdminSnap = await getDocs(
          query(collection(firestore, 's_admin'), where('email', '==', normalizedEmail))
        );
        const developerSnap = await getDocs(
          query(collection(firestore, 'developer'), where('email', '==', normalizedEmail))
        );

        if (alumniSnap.size > 0) {
          setUserRole('alumni');
        } else if (coachSnap.size > 0) {
          setUserRole('coach');
        } else if (clientSnap.size > 0) {
          setUserRole('client');
        } else if (adminSnap.size > 0) {
          setUserRole('admin');
        } else if (moderatorSnap.size > 0) {
          setUserRole('moderator');
        } else if (superAdminSnap.size > 0) {
          setUserRole('superAdmin');
        } else if (developerSnap.size > 0) {
          setUserRole('PlatformEngineer');
        } else {
          const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role);
          } else {
            setUserRole('client');
          }
        }
      } catch (error) {
        setUserRole('client');
      }
      setLoading(false);
    };

    fetchUserRole();
  }, [currentUser]);

  const value = {
    userRole,
    loading,
    isEntrepreneur: userRole === 'entrepreneur',
    isEntrepreneurs: userRole === 'entrepreneurs',

    isAlumni: userRole === 'alumni',
    isAlumnis: userRole === 'alumnis',

    isModerator: userRole === 'moderator',
    isModerators: userRole === 'moderators',

    isAdmin: userRole === 'admin',
    isAdmins: userRole === 'admins',

    isClient: userRole === 'client',
    isClients: userRole === 'clients',

    isCoach: userRole === 'coach',
    isCoaches: userRole === 'coaches',

    isBusinessCoach: userRole === 'businessCoach',
    isBusinessCoaches: userRole === 'businessCoaches',

    canAnswerFAQs: userRole === 'businessCoach' || userRole === 'alumni',
    canPostInnovation: userRole === 'businessCoach' || userRole === 'alumni',

    isSuperAdmin: userRole === 'superAdmin',
    isSuperAdmins: userRole === 'superAdmins',

    isPlatformEngineer: userRole === 'PlatformEngineer',
    isPlatformEngineers: userRole === 'PlatformEngineers',
  };

  return <UserRoleContext.Provider value={value}>{!loading && children}</UserRoleContext.Provider>;
};
